import { useState, useEffect, useRef } from "react";

import DashboardLayout from "../layouts/DashboardLayout";
import api from "../api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

// Searchable Select Component
function SearchableSelect({ options, value, onChange, placeholder, name }) {
    const [search, setSearch] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.value === value);

    // When closed, display selected label. When open, display search text.
    const displayValue = isOpen ? search : (selectedOption ? selectedOption.label : "");

    const filteredOptions = options.filter(o =>
        o.label.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div ref={wrapperRef} className="relative w-full">
            <input
                type="text"
                className="border p-2 w-full rounded focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder={placeholder}
                value={displayValue}
                onChange={e => {
                    setSearch(e.target.value);
                    if (!isOpen) setIsOpen(true);
                    if (value) {
                        onChange({ target: { name, value: "", type: "select-one" } });
                    }
                }}
                onClick={() => {
                    setSearch("");
                    setIsOpen(true);
                }}
            />
            {isOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-y-auto">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map(option => (
                            <div
                                key={option.value}
                                className={`p-2 cursor-pointer hover:bg-blue-50 ${option.value === value ? 'bg-blue-100 font-medium' : ''}`}
                                onClick={() => {
                                    onChange({ target: { name, value: option.value, type: "select-one" } });
                                    setSearch("");
                                    setIsOpen(false);
                                }}
                            >
                                {option.label}
                            </div>
                        ))
                    ) : (
                        <div className="p-2 text-gray-500 text-sm">No results found</div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function UploadSession() {

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [doctors, setDoctors] = useState([]);
    const [patients, setPatients] = useState([]);
    const [profileOptions, setProfileOptions] = useState([]);

    const [formData, setFormData] = useState({
        session_date: "",
        session_time: "",
        session_duration: "00:45:00",  // HH:MM:SS format
        therapy_mode: "CBT",
        doctor_id: "",
        patient_id: "",
        doctor_identifier: "Doctor:**",  // Default prefix for doctor in transcript
        patient_identifier: "Patient:**",  // Default prefix for patient in transcript
        transcript_format: "PLAIN_TEXT",

        // New Patient Fields
        is_new_patient: false,
        patient_name: "",
        patient_dob: "",  // intentionally blank — UI shows "Select Age", backend defaults to 31
        patient_issues: "",
        patient_contact: "",
        patient_gender: "",
        patient_relationshipstatus: "",
        patient_profile_type: "Unknown"
    });

    const [file, setFile] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [docRes, patRes, profileRes] = await Promise.all([
                api.fetchDoctors(),
                api.fetchPatients(),
                api.getAllVirtualPatientProfiles()
            ]);
            setDoctors((docRes.data && docRes.data.data) ? docRes.data.data : (docRes.data || []));
            setPatients((patRes.data && patRes.data.data) ? patRes.data.data : (patRes.data || []));

            // Extract profile keys from the response
            const profileData = profileRes.data || {};
            const patients = profileData.patients || {};
            setProfileOptions(Object.keys(patients));
        } catch (err) {
            console.error("Error fetching options", err);
            toast.error("Failed to load options");
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Handle format change to set default identifiers
        if (name === "transcript_format") {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                doctor_identifier: value === "JSON_V1" ? "therapist" : "Doctor:**",
                patient_identifier: value === "JSON_V1" ? "patient" : "Patient:**"
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) {
            toast.error("Please upload a transcript file");
            return;
        }
        if (!formData.doctor_id) {
            toast.error("Please select a clinician");
            return;
        }
        if (!formData.is_new_patient && !formData.patient_id) {
            toast.error("Please select a patient or create a new one");
            return;
        }
        if (formData.is_new_patient && !formData.patient_name) {
            toast.error("Patient Name is required");
            return;
        }

        setLoading(true);
        try {
            const payload = new FormData();
            payload.append("file", file);
            payload.append("doctor_id", formData.doctor_id);
            payload.append("session_date", formData.session_date);
            payload.append("session_time", formData.session_time);
            payload.append("session_duration", formData.session_duration);
            payload.append("therapy_mode", formData.therapy_mode);
            payload.append("doctor_identifier", formData.doctor_identifier);
            payload.append("patient_identifier", formData.patient_identifier);
            payload.append("patient_identifier", formData.patient_identifier);
            payload.append("transcript_format", formData.transcript_format);
            payload.append("evaluation_trigger", formData.evaluation_trigger || "immediate");

            if (formData.is_new_patient) {
                payload.append("new_patient_name", formData.patient_name);
                payload.append("new_patient_age", formData.patient_dob || "31");
                payload.append("patient_issues", formData.patient_issues);
                payload.append("new_patient_contact", formData.patient_contact);
                payload.append("new_patient_gender", formData.patient_gender);
                payload.append("patient_relationshipstatus", formData.patient_relationshipstatus);
                payload.append("patient_profile_type", formData.patient_profile_type);
            } else {
                payload.append("patient_id", formData.patient_id);
            }

            await api.uploadSession(payload);
            toast.success("Session uploaded successfully");
            navigate("/consultationhistory"); // Redirect to history or dashboard
        } catch (err) {
            console.error("Upload error", err);
            toast.error(err.response?.data?.error || "Failed to upload session");
        } finally {
            setLoading(false);
        }
    };

    return (
        <DashboardLayout>
            <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-xl">
                <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100 border-b pb-2">Upload Therapy Transcript</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Format Selection - New Field */}
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
                        <label className="block text-sm font-semibold mb-2 text-gray-800">Transcript Format</label>
                        <select
                            name="transcript_format"
                            value={formData.transcript_format || "PLAIN_TEXT"}
                            onChange={handleChange}
                            className="border p-2 w-full rounded focus:ring-2 focus:ring-blue-500 outline-none mb-4"
                        >
                            <option value="PLAIN_TEXT">General (Plain Text)</option>
                            <option value="JSON_V1">Json format - 1</option>
                        </select>

                        {/* Sample Display */}
                        <div className="bg-slate-800 text-slate-200 p-3 rounded text-xs font-mono overflow-x-auto">
                            <p className="mb-2 text-slate-400 font-bold uppercase">Sample Structure:</p>
                            {formData.transcript_format === "JSON_V1" ? (
                                <pre>{`"transcript": [
  {
    "seq": 0,
    "persona": "patient",
    "text": "Hi"
  },
  {
    "seq": 1,
    "persona": "therapist",
    "text": "Hello, good morning. How are you feeling today?"
  }
]`}</pre>
                            ) : (
                                <pre>{`Doctor:** Hello, how are you today?
Patient:** I am feeling a bit anxious.
Doctor:** Can you tell me more about that?`}</pre>
                            )}
                        </div>
                    </div>

                    {/* Evaluation Trigger - New Field */}
                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-100 mb-6">
                        <label className="block text-sm font-semibold mb-2 text-purple-800">Evaluation Trigger</label>
                        <div className="flex space-x-6">
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="evaluation_trigger"
                                    value="immediate"
                                    checked={formData.evaluation_trigger !== "manual"}
                                    onChange={handleChange}
                                    className="text-purple-600 focus:ring-purple-500"
                                />
                                <div>
                                    <span className="font-medium text-gray-800">Automatic (Immediate)</span>
                                    <p className="text-xs text-gray-500">Run full clinical audit now (~3 mins)</p>
                                </div>
                            </label>

                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="evaluation_trigger"
                                    value="manual"
                                    checked={formData.evaluation_trigger === "manual"}
                                    onChange={handleChange}
                                    className="text-purple-600 focus:ring-purple-500"
                                />
                                <div>
                                    <span className="font-medium text-gray-800">Manual (Audit Later)</span>
                                    <p className="text-xs text-gray-500">Save only; trigger audit manually later</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* File Upload */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <label className="block text-sm font-semibold mb-2 text-blue-800">
                            Transcript File ({formData.transcript_format === "JSON_V1" ? ".json" : ".txt"})
                        </label>
                        <input
                            type="file"
                            accept={formData.transcript_format === "JSON_V1" ? ".json" : ".txt"}
                            onChange={handleFileChange}
                            className="block w-full text-sm text-slate-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-full file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-600 file:text-white
                hover:file:bg-blue-700"
                        />
                        <p className="text-xs text-blue-600 mt-2">
                            Upload the {formData.transcript_format === "JSON_V1" ? "JSON" : "text"} transcript file here. Required.
                        </p>
                    </div>

                    {/* Transcript Identifiers - Full Width Row */}
                    <div>
                        <label className="block text-sm font-medium mb-2">Transcript Speaker Identifiers</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">Clinician Identifier</label>
                                <input
                                    type="text"
                                    name="doctor_identifier"
                                    value={formData.doctor_identifier}
                                    onChange={handleChange}
                                    placeholder={formData.transcript_format === "JSON_V1" ? "e.g., clinician" : "e.g., **Clinician:**"}
                                    className="border p-2 w-full rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {formData.transcript_format === "JSON_V1"
                                        ? "Value of 'persona' key for clinician in JSON"
                                        : "How clinician lines start in transcript"}
                                </p>
                            </div>
                            <div>
                                <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">Patient Identifier</label>
                                <input
                                    type="text"
                                    name="patient_identifier"
                                    value={formData.patient_identifier}
                                    onChange={handleChange}
                                    placeholder={formData.transcript_format === "JSON_V1" ? "e.g., member" : "e.g., Patient:**"}
                                    className="border p-2 w-full rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {formData.transcript_format === "JSON_V1"
                                        ? "Value of 'persona' key for patient in JSON"
                                        : "How patient lines start in transcript"}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Session Info - 3 Column Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Session Date */}
                        <div>
                            <label className="block text-sm font-medium mb-1">Session Date</label>
                            <input
                                type="date"
                                name="session_date"
                                value={formData.session_date}
                                onChange={handleChange}
                                max={new Date().toISOString().split('T')[0]}
                                className="border p-2 w-full rounded focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Cannot be in the future</p>
                        </div>

                        {/* Session Time */}
                        <div>
                            <label className="block text-sm font-medium mb-1">Session Start Time</label>
                            <input
                                type="time"
                                name="session_time"
                                value={formData.session_time}
                                onChange={handleChange}
                                className="border p-2 w-full rounded focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Start time of the session</p>
                        </div>

                        {/* Session Duration */}
                        <div>
                            <label className="block text-sm font-medium mb-1">Duration (HH:MM:SS) *</label>
                            <input
                                type="text"
                                name="session_duration"
                                value={formData.session_duration}
                                onChange={handleChange}
                                placeholder="00:45:00"
                                pattern="[0-9]{2}:[0-9]{2}:[0-9]{2}"
                                title="Format: HH:MM:SS (e.g., 01:30:00)"
                                className="border p-2 w-full rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                required
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Format: HH:MM:SS (e.g., 01:30:00 for 1 hour 30 minutes)</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Therapy Mode</label>
                            <select
                                name="therapy_mode"
                                value={formData.therapy_mode}
                                onChange={handleChange}
                                disabled
                                className="border p-2 w-full rounded bg-gray-100 text-gray-500 cursor-not-allowed"
                            >
                                <option value="CBT">CBT</option>
                                <option value="REBT">REBT</option>
                                <option value="SFBT">SFBT</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Clinician</label>
                            <select
                                name="doctor_id"
                                value={formData.doctor_id}
                                onChange={handleChange}
                                className="border p-2 w-full rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                required
                            >
                                <option value="">Select Clinician</option>
                                {doctors.map(d => (
                                    <option key={d.doctor_id} value={d.doctor_id}>Dr. {d.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <hr className="my-4" />

                    {/* Patient Selection */}
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Patient Details</h3>
                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    name="is_new_patient"
                                    checked={formData.is_new_patient}
                                    onChange={handleChange}
                                    id="newPatientToggle"
                                    className="w-4 h-4 text-blue-600 rounded"
                                />
                                <label htmlFor="newPatientToggle" className="text-sm font-medium cursor-pointer select-none">Onboard New Patient</label>
                            </div>
                        </div>

                        {!formData.is_new_patient ? (
                            <div className="transition-opacity duration-300">
                                <label className="block text-sm font-medium mb-1">Select Existing Patient</label>
                                <SearchableSelect
                                    name="patient_id"
                                    value={formData.patient_id}
                                    onChange={handleChange}
                                    placeholder="Search/Select Patient..."
                                    options={patients.map(p => ({
                                        value: p.id,
                                        label: p.contact ? `${p.name} (${p.contact})` : p.name
                                    }))}
                                />
                            </div>
                        ) : (
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 animate-fadeIn">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Patient Identifier *</label>
                                        <input
                                            name="patient_name"
                                            type="text"
                                            value={formData.patient_name}
                                            onChange={handleChange}
                                            className="border p-2 w-full rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                            required
                                        />
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Unique Patient Identifier</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Age</label>
                                        <select name="patient_dob" value={formData.patient_dob} onChange={handleChange} className="border p-2 w-full rounded">
                                            <option value="">Select Age</option>
                                            {Array.from({ length: 91 }, (_, i) => i + 10).map(age => (
                                                <option key={age} value={age}>{age}</option>
                                            ))}
                                        </select>

                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Cannot be in the future</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Contact Number</label>
                                        <input
                                            name="patient_contact"
                                            type="text"
                                            value={formData.patient_contact}
                                            onChange={handleChange}
                                            placeholder="e.g., +1 234 567 8900"
                                            className="border p-2 w-full rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Optional contact number</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Gender *</label>
                                        <select name="patient_gender" value={formData.patient_gender} onChange={handleChange} className="border p-2 w-full rounded" required>
                                            <option value="">Select Gender</option>
                                            <option>Male</option>
                                            <option>Female</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Relationship Status *</label>
                                        <select name="patient_relationshipstatus" value={formData.patient_relationshipstatus} onChange={handleChange} className="border p-2 w-full rounded" required>
                                            <option value="">Select Status</option>
                                            <option>Married</option>
                                            <option>In Relationship</option>
                                            <option>Single</option>
                                            <option>Divorce</option>
                                            <option>Unknown</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Profile Type (Virtual Patient)</label>
                                        <select name="patient_profile_type" value={formData.patient_profile_type} onChange={handleChange} className="border p-2 w-full rounded">
                                            <option value="">Select Profile</option>
                                            {profileOptions.map(opt => (
                                                <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium mb-1">Presenting Issues</label>
                                        <textarea name="patient_issues" value={formData.patient_issues} onChange={handleChange} className="border p-2 w-full rounded" rows="2"></textarea>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full py-3 px-4 rounded-lg text-white font-semibold transition-colors
                ${loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-md'}
              `}
                        >
                            {loading ? "Uploading..." : "Upload Transcript"}
                        </button>
                    </div>

                </form>
            </div>
        </DashboardLayout>
    );
}
